import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Check,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Plus,
  Tag,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { celebrationTagService } from '@/lib/api/celebrationTag.service';
import { tagCategoryService } from '@/lib/api/tagCategory.service';
import type {
  AITagSuggestion,
  ProcessedAISuggestion,
  CelebrationTag,
  TagsSnapshot,
  TagConfidence,
  TagCategoryWithValues,
} from '@/types/tag.types';

interface TagSuggestionPanelProps {
  celebrationId: number;
  onTagsConfirmed?: (snapshot: TagsSnapshot) => void;
  onTagsUnconfirmed?: () => void;
}

interface SuggestionState {
  /** AI analysis raw suggestions */
  suggestions: AITagSuggestion[];
  /** Resolved tag_value_ids from AI */
  resolvedTags: ProcessedAISuggestion[];
  /** Follow-up questions from AI */
  followUpQuestions: string[];
  /** IDs that the user has accepted */
  acceptedIds: Set<number>;
  /** IDs that the user has rejected */
  rejectedIds: Set<number>;
}

const confidenceColors: Record<TagConfidence, string> = {
  high: 'bg-green-100 text-green-800 border-green-300',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  low: 'bg-gray-100 text-gray-600 border-gray-300',
};

export const TagSuggestionPanel: React.FC<TagSuggestionPanelProps> = ({
  celebrationId,
  onTagsConfirmed,
  onTagsUnconfirmed,
}) => {
  const { t } = useTranslation('celebrations');
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyzed, setAnalyzed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [savingTags, setSavingTags] = useState(false);

  const [state, setState] = useState<SuggestionState>({
    suggestions: [],
    resolvedTags: [],
    followUpQuestions: [],
    acceptedIds: new Set(),
    rejectedIds: new Set(),
  });

  // Current tags on the celebration
  const [currentTags, setCurrentTags] = useState<CelebrationTag[]>([]);
  const [originalTagIds, setOriginalTagIds] = useState<Set<number>>(new Set());
  const [loadingTags, setLoadingTags] = useState(false);

  // Manual tag input
  const [manualCategory, setManualCategory] = useState('');
  const [manualValue, setManualValue] = useState('');
  const [addingManual, setAddingManual] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [categories, setCategories] = useState<TagCategoryWithValues[]>([]);

  // Load existing tags and categories on mount
  useEffect(() => {
    const loadInitialState = async () => {
      setLoadingTags(true);
      try {
        const [tags, status, cats] = await Promise.all([
          celebrationTagService.getTags(celebrationId),
          celebrationTagService.getTagsStatus(celebrationId).catch(() => ({ tags_confirmed: false })),
          tagCategoryService.getCategoriesWithValues().catch(() => []),
        ]);
        setCurrentTags(tags);
        setOriginalTagIds(new Set(tags.map(t => t.tag_value_id)));
        setCategories(cats);
        if (status.tags_confirmed) {
          setConfirmed(true);
          setAnalyzed(true);
        }
      } catch {
        // non-critical
      } finally {
        setLoadingTags(false);
      }
    };
    loadInitialState();
  }, [celebrationId]);

  /**
   * Run AI analysis on celebration paragraphs
   */
  const runAnalysis = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      const result = await celebrationTagService.analyzeParagraphs(celebrationId);

      // All suggestions start as pending (not accepted/rejected)
      setState({
        suggestions: result.suggestions,
        resolvedTags: result.resolved_tags,
        followUpQuestions: result.follow_up_questions,
        acceptedIds: new Set(),
        rejectedIds: new Set(),
      });
      setAnalyzed(true);

      // Load current tags
      await loadCurrentTags();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'AI analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const loadCurrentTags = async () => {
    setLoadingTags(true);
    try {
      const tags = await celebrationTagService.getTags(celebrationId);
      setCurrentTags(tags);
      setOriginalTagIds(new Set(tags.map(t => t.tag_value_id)));
    } catch {
      // non-critical
    } finally {
      setLoadingTags(false);
    }
  };

  const addTagToDraft = (tag: CelebrationTag) => {
    setCurrentTags(prev => {
      if (prev.some(t => t.tag_value_id === tag.tag_value_id)) {
        return prev;
      }
      return [...prev, tag];
    });
  };

  const createDraftTagFromResolved = (tag: ProcessedAISuggestion): CelebrationTag => {
    const category = categories.find(c => c.name.toLowerCase() === tag.category.toLowerCase());
    const value = category?.values?.find(v => v.id === tag.tag_value_id);

    return {
      id: -tag.tag_value_id,
      celebration_id: celebrationId,
      tag_value_id: tag.tag_value_id,
      source: 'ai_accepted',
      confidence: (tag.confidence as TagConfidence) || 'medium',
      created_at: new Date().toISOString(),
      category_name: tag.category,
      category_id: category?.id || value?.category_id || 0,
      tag_value: tag.value,
      is_system: value?.is_system || false,
    };
  };

  const saveTagChanges = async (): Promise<boolean> => {
    const currentIds = new Set(currentTags.map(t => t.tag_value_id));
    const idsToAdd = Array.from(currentIds).filter(id => !originalTagIds.has(id));
    const idsToRemove = Array.from(originalTagIds).filter(id => !currentIds.has(id));

    if (idsToAdd.length === 0 && idsToRemove.length === 0) {
      return true;
    }

    setSavingTags(true);
    setError(null);
    try {
      for (const tagValueId of idsToRemove) {
        await celebrationTagService.removeTag(celebrationId, tagValueId);
      }

      if (idsToAdd.length > 0) {
        await celebrationTagService.assignTags(celebrationId, {
          tag_value_ids: idsToAdd,
          source: 'manual',
        });
      }

      await loadCurrentTags();
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save tag changes');
      await loadCurrentTags();
      return false;
    } finally {
      setSavingTags(false);
    }
  };

  /**
   * Accept a suggested tag — assign it to the celebration
   */
  const acceptTag = async (tag: ProcessedAISuggestion) => {
    try {
      setState(prev => ({
        ...prev,
        acceptedIds: new Set([...prev.acceptedIds, tag.tag_value_id]),
        rejectedIds: new Set([...prev.rejectedIds].filter(id => id !== tag.tag_value_id)),
      }));

      addTagToDraft(createDraftTagFromResolved(tag));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to accept tag');
      // Rollback
      setState(prev => ({
        ...prev,
        acceptedIds: new Set([...prev.acceptedIds].filter(id => id !== tag.tag_value_id)),
      }));
    }
  };

  /**
   * Reject a suggestion
   */
  const rejectTag = async (tag: ProcessedAISuggestion) => {
    // If already accepted, remove from draft
    if (state.acceptedIds.has(tag.tag_value_id)) {
      setCurrentTags(prev => prev.filter(t => t.tag_value_id !== tag.tag_value_id));
    }

    setState(prev => ({
      ...prev,
      rejectedIds: new Set([...prev.rejectedIds, tag.tag_value_id]),
      acceptedIds: new Set([...prev.acceptedIds].filter(id => id !== tag.tag_value_id)),
    }));
  };

  /**
   * Accept ALL pending suggestions at once
   */
  const acceptAll = async () => {
    const pending = state.resolvedTags.filter(
      t => !state.acceptedIds.has(t.tag_value_id) && !state.rejectedIds.has(t.tag_value_id)
    );

    if (pending.length === 0) return;

    const ids = pending.map(p => p.tag_value_id);
    try {
      setState(prev => ({
        ...prev,
        acceptedIds: new Set([...prev.acceptedIds, ...ids]),
      }));

      const tagsToAdd = pending.map(createDraftTagFromResolved);
      setCurrentTags(prev => {
        const existingIds = new Set(prev.map(t => t.tag_value_id));
        const newTags = tagsToAdd.filter(t => !existingIds.has(t.tag_value_id));
        return [...prev, ...newTags];
      });
    } catch (err: any) {
      setError('Failed to accept all tags');
    }
  };

  /**
   * Confirm tags — this unlocks event creation
   */
  const confirmTags = async () => {
    setConfirming(true);
    setError(null);
    try {
      const result = await celebrationTagService.confirmTags(celebrationId);
      setConfirmed(true);
      onTagsConfirmed?.(result.snapshot);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to confirm tags');
    } finally {
      setConfirming(false);
    }
  };

  /**
   * Remove a currently assigned tag
   */
  const removeCurrentTag = async (tagValueId: number) => {
    setCurrentTags(prev => prev.filter(t => t.tag_value_id !== tagValueId));
  };

  /**
   * Add a manual tag by category name and value
   */
  const addManualTag = async () => {
    if (!manualCategory.trim() || !manualValue.trim()) return;
    setAddingManual(true);
    setError(null);
    try {
      // Find or create the category
      let category = categories.find(
        c => c.name.toLowerCase() === manualCategory.trim().toLowerCase()
      );
      if (!category) {
        category = await tagCategoryService.createCategory({ name: manualCategory.trim() }) as TagCategoryWithValues;
        setCategories(prev => [...prev, { ...category!, values: [] }]);
      }

      // Find or create the value
      let value = category.values?.find(
        v => v.value.toLowerCase() === manualValue.trim().toLowerCase()
      );
      if (!value) {
        value = await tagCategoryService.addValue(category.id, { value: manualValue.trim() });
      }

      addTagToDraft({
        id: -value.id,
        celebration_id: celebrationId,
        tag_value_id: value.id,
        source: 'manual',
        created_at: new Date().toISOString(),
        category_name: category.name,
        category_id: category.id,
        tag_value: value.value,
        is_system: value.is_system,
      });

      setManualCategory('');
      setManualValue('');
      setShowManualForm(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add manual tag');
    } finally {
      setAddingManual(false);
    }
  };

  // Group suggestions by category
  const groupedSuggestions = state.suggestions.reduce<
    Record<string, { suggestion: AITagSuggestion; resolved: ProcessedAISuggestion[] }>
  >((acc, suggestion) => {
    const resolved = state.resolvedTags.filter(r => r.category === suggestion.category);
    acc[suggestion.category] = { suggestion, resolved };
    return acc;
  }, {});

  const pendingCount = state.resolvedTags.filter(
    t => !state.acceptedIds.has(t.tag_value_id) && !state.rejectedIds.has(t.tag_value_id)
  ).length;
  const currentTagIds = new Set(currentTags.map(t => t.tag_value_id));
  const hasUnsavedChanges =
    currentTagIds.size !== originalTagIds.size ||
    Array.from(currentTagIds).some(id => !originalTagIds.has(id));

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{t('tagSuggestions.title')}</CardTitle>
            {!confirmed && hasUnsavedChanges && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-300 text-amber-700 bg-amber-50">
                {t('tagSuggestions.unsavedChanges')}
              </Badge>
            )}
          </div>
          {confirmed && (
            <Badge variant="default">
              <CheckCircle2 className="h-3 w-3 mr-1" /> {t('tagSuggestions.tagsConfirmed')}
            </Badge>
          )}
        </div>
        <CardDescription>
          {!analyzed
            ? t('tagSuggestions.descriptionBefore')
            : t('tagSuggestions.descriptionAfter')}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Error display */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Analysis button */}
        {!analyzed && !loadingTags && (
          <Button onClick={runAnalysis} disabled={analyzing} className="w-full">
            {analyzing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('tagSuggestions.analyzing')}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                {t('tagSuggestions.analyzeWithAI')}
              </>
            )}
          </Button>
        )}

        {loadingTags && !analyzed && (
          <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {t('tagSuggestions.loadingTags')}
          </div>
        )}

        {/* Suggestions grouped by category */}
        {analyzed && Object.keys(groupedSuggestions).length > 0 && (
          <>
            {/* Bulk actions */}
            {pendingCount > 0 && !confirmed && (
              <div className="flex items-center justify-between p-2 bg-muted rounded-lg">
                <span className="text-sm text-muted-foreground">
                  {t('tagSuggestions.pendingSuggestions', { count: pendingCount })}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={acceptAll}>
                    <Check className="h-3 w-3 mr-1" /> {t('tagSuggestions.acceptAll')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={runAnalysis} disabled={analyzing}>
                    <Sparkles className="h-3 w-3 mr-1" /> {t('tagSuggestions.reAnalyze')}
                  </Button>
                </div>
              </div>
            )}

            {/* Category groups */}
            {Object.entries(groupedSuggestions).map(([category, { suggestion, resolved }]) => (
              <div key={category} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{category}</span>
                  {suggestion.is_new_category && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{t('tagSuggestions.newCategory')}</Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {resolved.map(tag => {
                    const isAccepted = state.acceptedIds.has(tag.tag_value_id);
                    const isRejected = state.rejectedIds.has(tag.tag_value_id);
                    const isPending = !isAccepted && !isRejected;

                    return (
                      <div
                        key={tag.tag_value_id}
                        className={`
                          inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium
                          transition-all
                          ${isAccepted ? 'bg-green-100 text-green-800 border-green-300' : ''}
                          ${isRejected ? 'bg-red-50 text-red-400 border-red-200 line-through opacity-60' : ''}
                          ${isPending ? confidenceColors[tag.confidence as TagConfidence] || confidenceColors.medium : ''}
                        `}
                      >
                        {tag.value}
                        {!confirmed && (
                          <span className="flex items-center gap-0.5 ml-1">
                            {!isAccepted && (
                              <button
                                onClick={() => acceptTag(tag)}
                                className="p-0.5 rounded-full hover:bg-green-200 transition-colors"
                                title="Accept"
                              >
                                <Check className="h-3 w-3 text-green-700" />
                              </button>
                            )}
                            {!isRejected && (
                              <button
                                onClick={() => rejectTag(tag)}
                                className="p-0.5 rounded-full hover:bg-red-200 transition-colors"
                                title="Reject"
                              >
                                <X className="h-3 w-3 text-red-600" />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Follow-up questions */}
        {state.followUpQuestions.length > 0 && (
          <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-1.5 text-sm font-medium text-blue-800">
              <HelpCircle className="h-4 w-4" />
              {t('tagSuggestions.couldAddDetail')}
            </div>
            <ul className="space-y-1 text-sm text-blue-700">
              {state.followUpQuestions.map((q, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-blue-400 mt-0.5">•</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Current tags display */}
        {currentTags.length > 0 && (
          <div className="space-y-2 pt-2 border-t">
            <span className="text-sm font-medium text-muted-foreground">{t('tagSuggestions.assignedTags')}</span>
            <div className="flex flex-wrap gap-1.5">
              {currentTags.map(tag => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className="flex items-center gap-1 pr-1"
                >
                  <span className="text-[10px] text-muted-foreground">{tag.category_name}:</span>
                  {tag.tag_value}
                  {!confirmed && (
                    <button
                      onClick={() => removeCurrentTag(tag.tag_value_id)}
                      className="ml-0.5 p-0.5 rounded-full hover:bg-destructive/10"
                      title="Remove"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Manual tag addition */}
        {!confirmed && (
          <div className="space-y-2 pt-2 border-t">
            {!showManualForm ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowManualForm(true)}
                className="w-full"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                {t('tagSuggestions.addTagManually')}
              </Button>
            ) : (
              <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t('tagSuggestions.addManualTag')}</span>
                  <button
                    onClick={() => { setShowManualForm(false); setManualCategory(''); setManualValue(''); }}
                    className="p-1 rounded hover:bg-muted"
                    title="Close manual tag form"
                    aria-label="Close manual tag form"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      placeholder={t('tagSuggestions.categoryPlaceholder')}
                      value={manualCategory}
                      onChange={(e) => setManualCategory(e.target.value)}
                      list="tag-categories-list"
                      compact
                    />
                    <datalist id="tag-categories-list">
                      {categories.map(c => (
                        <option key={c.id} value={c.name} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <Input
                      placeholder={t('tagSuggestions.valuePlaceholder')}
                      value={manualValue}
                      onChange={(e) => setManualValue(e.target.value)}
                      list={`tag-values-${manualCategory}`}
                      compact
                    />
                    <datalist id={`tag-values-${manualCategory}`}>
                      {categories
                        .find(c => c.name.toLowerCase() === manualCategory.toLowerCase())
                        ?.values?.map(v => (
                          <option key={v.id} value={v.value} />
                        ))}
                    </datalist>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={addManualTag}
                  disabled={addingManual || !manualCategory.trim() || !manualValue.trim()}
                  className="w-full"
                >
                  {addingManual ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Plus className="h-3 w-3 mr-1" />
                  )}
                  {t('tagSuggestions.addTag')}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Confirm button */}
        {analyzed && !confirmed && (
          <div className="space-y-2">
            <Button
              onClick={saveTagChanges}
              disabled={savingTags || !hasUnsavedChanges}
              className="w-full"
              variant="outline"
            >
              {savingTags ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('tagSuggestions.savingTags')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t('tagSuggestions.saveTags')}
                </>
              )}
            </Button>

            <Button
              onClick={async () => {
                const saved = await saveTagChanges();
                if (!saved) return;
                await confirmTags();
              }}
              disabled={confirming || savingTags || currentTags.length === 0}
              className="w-full"
              variant="default"
            >
              {confirming ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('tagSuggestions.confirming')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('tagSuggestions.confirmTags')}
                  {currentTags.length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {new Set(currentTags.map(t => t.tag_value_id)).size}
                    </Badge>
                  )}
                </>
              )}
            </Button>
          </div>
        )}

        {/* Confirmed state — view tags with edit option */}
        {confirmed && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
              <CheckCircle2 className="h-4 w-4" />
              {t('tagSuggestions.tagsConfirmedMessage')}
            </div>

            {/* Display confirmed tags grouped by category */}
            {currentTags.length > 0 && (() => {
              const grouped: Record<string, string[]> = {};
              currentTags.forEach((t) => {
                if (!grouped[t.category_name]) grouped[t.category_name] = [];
                if (!grouped[t.category_name].includes(t.tag_value)) {
                  grouped[t.category_name].push(t.tag_value);
                }
              });
              return (
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Tag className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">{t('tagSuggestions.confirmedTags')}</span>
                    <Badge variant="secondary" className="text-xs">{currentTags.length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(grouped).map(([category, values]) => (
                      <div key={category} className="flex items-start gap-2">
                        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap mt-0.5">{category}:</span>
                        <div className="flex flex-wrap gap-1">
                          {values.map((value) => (
                            <Badge key={value} variant="secondary" className="text-xs">
                              {value}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <Button
              onClick={async () => {
                try {
                  await celebrationTagService.unconfirmTags(celebrationId);
                } catch {
                  // proceed with UI reset even if backend call fails
                }
                setConfirmed(false);
                setAnalyzed(false);
                setState({
                  suggestions: [],
                  resolvedTags: [],
                  followUpQuestions: [],
                  acceptedIds: new Set(),
                  rejectedIds: new Set(),
                });
                onTagsUnconfirmed?.();
              }}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <RefreshCw className="h-3 w-3 mr-2" />
              Edit paragraphs & re-analyze
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
